import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type PendingApproval } from '../lib/api';
import { errorMessage } from '../lib/format';
import { ToolForm } from './tool-form';

/**
 * D6 approval gate, user side. The agent's order-placing call is parked in the workflow; this card shows
 * the exact arguments (editable), and Approve / Deny signals the decision. The control plane executes an
 * approved call once; the card disappears from `session.pending` either way and the outcome shows up as
 * the tool result in the transcript.
 */
export function ApprovalCard({ threadId, item }: { threadId: string; item: PendingApproval }) {
  const detail = useQuery({
    queryKey: ['connector', item.connector],
    queryFn: () => api.connector(item.connector),
    staleTime: 60_000,
  });
  const [sent, setSent] = useState<'approve' | 'deny' | null>(null);
  const decide = useMutation({
    mutationFn: (p: { decision: 'approve' | 'deny'; args?: Record<string, unknown> }) =>
      api.decide(threadId, item.callId, p.decision, p.args),
    onSuccess: (_r, p) => setSent(p.decision),
  });
  const spec = detail.data?.tools.find((t) => t.name === item.tool);
  return (
    <div className="execute stack" data-testid="approval-card">
      <h3>
        Approve{' '}
        <code>
          {item.connector}.{item.tool}
        </code>
        ?
      </h3>
      <p className="muted">
        This call can place or change orders, so the agent is waiting for you. Review the arguments,
        edit if needed, then approve to run it once or deny to stop it. Nothing happens until you
        decide.
      </p>
      {sent ? (
        <p className={sent === 'approve' ? 'ok' : 'muted'}>
          {sent === 'approve' ? 'Approved — executing…' : 'Denied — the agent has been told.'}
        </p>
      ) : (
        <>
          <ToolForm
            schema={spec?.inputSchema}
            initial={item.args}
            busy={decide.isPending}
            submitLabel={decide.isPending ? 'Sending…' : `Approve ${item.tool}`}
            onSubmit={(args) => decide.mutate({ decision: 'approve', args })}
          />
          <div className="row">
            <button
              type="button"
              className="danger"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'deny' })}
            >
              Deny
            </button>
          </div>
        </>
      )}
      {decide.error && <p className="error">{errorMessage(decide.error)}</p>}
    </div>
  );
}
